import { domToCanvas } from "modern-screenshot";

const MAX_BYTES = 4 * 1024 * 1024; // marge sous la limite serveur de 5 Mo

export interface ScreenshotResult {
  blob: Blob;
  dataUrl: string;
}

/**
 * Capture le viewport en image (sans dialogue de partage d'écran).
 * L'UI du widget (nœuds portant [data-bug-widget]) est exclue du rendu.
 * Si un rect est fourni (coordonnées viewport), l'image est recadrée dessus —
 * comme l'outil de capture de zone de l'OS.
 * Renvoie null en cas d'échec — le signalement continue sans capture.
 */
export async function captureScreenshot(
  region?: { x: number; y: number; width: number; height: number },
): Promise<ScreenshotResult | null> {
  try {
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const full = await domToCanvas(document.body, {
      scale,
      backgroundColor: "#ffffff",
      filter: (node) =>
        !(node instanceof Element && node.hasAttribute("data-bug-widget")),
    });

    // Le rendu couvre tout le body : on recadre sur le viewport courant,
    // ou sur la zone choisie par l'utilisateur (clampée au viewport).
    const crop = region
      ? {
          x: Math.max(0, region.x),
          y: Math.max(0, region.y),
          width: Math.min(region.width, window.innerWidth - Math.max(0, region.x)),
          height: Math.min(region.height, window.innerHeight - Math.max(0, region.y)),
        }
      : { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight };
    if (crop.width < 1 || crop.height < 1) return null;

    const bodyRect = document.body.getBoundingClientRect();
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(crop.width * scale);
    canvas.height = Math.round(crop.height * scale);
    const cropCtx = canvas.getContext("2d");
    if (!cropCtx) return null;
    cropCtx.fillStyle = "#ffffff";
    cropCtx.fillRect(0, 0, canvas.width, canvas.height);
    cropCtx.drawImage(
      full,
      (crop.x - bodyRect.left) * scale,
      (crop.y - bodyRect.top) * scale,
      canvas.width,
      canvas.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    for (const quality of [0.82, 0.6, 0.4]) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", quality),
      );
      if (blob && blob.size <= MAX_BYTES) {
        return { blob, dataUrl: canvas.toDataURL("image/jpeg", quality) };
      }
    }
    return null;
  } catch {
    return null;
  }
}
